// Appointment.tsx
import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { CompositeFilterDescriptor } from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiChevronDown, FiChevronUp, FiSearch, FiX } from "react-icons/fi";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog } from "../../components/dialog/dialogSlice";
import {
  deleteAppointment,
  getAllAppointments,
} from "./services/appointment.service";
import {
  clearAppointmentID,
  clearFilterAppointment,
  setFilterAppointment,
} from "./appointmentSlice";
import IconComponent from "../../components/common/IconComponent";
import { Field, Form, FormElement } from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import FormDatePicker from "../../components/formFields/FormDateField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { getAllActiveDoctors } from "../doctor/services/doctor.service";
import moment from "moment";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";

const AppointmentFilterCard: React.FC = () => {
  const dispatch = useAppDispatch();
  const [collapsed, setCollapsed] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const DoctorList = useAppSelector((state) => state.doctor.DoctorList);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterAppointment = useAppSelector(
    (state) => state.appointment.FilterAppointment,
  );

  // useEffect(() => {
  //   setFormKey((prev) => prev + 1);
  // }, [DoctorList]);

  const handleSubmit = (values: any) => {
    dispatch(
      setFilterAppointment({
        ...values,
        FromDate: values.FromDate
          ? moment(values.FromDate).format("YYYY-MM-DD")
          : "",
        ToDate: values.ToDate
          ? moment(FilterAppointment.ToDate).format("YYYY-MM-DD")
          : "",
        Skip: 0,
        Limit: 10,
      }),
    );
  };

  const handleClear = () => {
    dispatch(clearFilterAppointment());
    dispatch(setCurrentPage(0));
    dispatch(setPageLimit(10));
    setFormKey((prev) => prev + 1);
  };

  useEffect(() => {
    dispatch(
      getAllAppointments({
        ...FilterAppointment,
        Skip: currentPage * pageLimit,
        Limit: pageLimit,
      }),
    );
  }, [FilterAppointment, currentPage, pageLimit]);

  return (
    <div
      style={{
        border: "1px solid #dee2e6",
        borderRadius: 8,
        marginBottom: 16,
        backgroundColor: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => setCollapsed((c) => !c)}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 18px",
          cursor: "pointer",
          backgroundColor: "#f8f9fa",
          borderBottom: collapsed ? "none" : "1px solid #dee2e6",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <IconComponent icon={FiSearch} />
          <Typography.h5 style={{ margin: 0, color: "#495057", fontSize: 15 }}>
            Filter Appointments
          </Typography.h5>
        </div>
        {collapsed ? (
          <IconComponent icon={FiChevronDown} />
        ) : (
          <IconComponent icon={FiChevronUp} />
        )}
      </div>

      {/* Card body — collapsible */}
      {!collapsed && (
        <div style={{ padding: "16px 18px" }}>
          <Form
            key={formKey}
            initialValues={{
              ...FilterAppointment,
              FromDate: FilterAppointment.FromDate
                ? moment(FilterAppointment.FromDate, "YYYY-MM-DD").toDate()
                : null,
              ToDate: FilterAppointment.ToDate
                ? moment(FilterAppointment.ToDate, "YYYY-MM-DD").toDate()
                : null,
            }}
            onSubmit={handleSubmit}
            render={() => (
              <FormElement style={{ width: "100%" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(300px, 1fr))",
                    gap: "14px 20px",
                  }}
                >
                  <Field
                    name="PatientName"
                    label="Patient Name"
                    placeholder="Search patient..."
                    component={FormTextField}
                  />

                  <Field
                    name="DoctorID"
                    label="Doctor Name"
                    placeholder="Search doctor..."
                    component={FormSelectionField}
                    options={DoctorList?.map((item) => {
                      return {
                        value: item.ID,
                        label: item.DoctorName,
                      };
                    })}
                  />

                  <Field
                    name="MobileNo"
                    label="Mobile No"
                    placeholder="Search mobile..."
                    component={FormTextField}
                  />

                  <Field
                    name="EmailID"
                    label="Email ID"
                    placeholder="Search email..."
                    component={FormTextField}
                  />

                  <Field
                    name="Fees"
                    label="Fees"
                    placeholder="Filter by fees..."
                    component={FormTextField}
                    type="number"
                  />

                  <Field
                    name="FromDate"
                    label="From Date"
                    component={FormDatePicker}
                  />

                  <Field
                    name="ToDate"
                    label="To Date"
                    component={FormDatePicker}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 10,
                    marginTop: 16,
                    paddingTop: 14,
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <Button
                    type="button"
                    fillMode="outline"
                    themeColor="secondary"
                    onClick={handleClear}
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <IconComponent icon={FiX} />
                    Clear
                  </Button>
                  <Button
                    type="submit"
                    fillMode="solid"
                    themeColor="primary"
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <IconComponent icon={FiSearch} />
                    Search
                  </Button>
                </div>
              </FormElement>
            )}
          />
        </div>
      )}
    </div>
  );
};

const Appointment: React.FC = () => {
  const dispatch = useAppDispatch();
  // const navigate = useNavigate();
  // const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    // dispatch(getAllAppointments({ ...FilterAppointment }));
    dispatch(getAllActiveDoctors());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  return (
    <>
      <DeleteAppointmentDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        {/* Title row */}
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 16px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Appointment List
          </Typography.h4>
          {/* {isAddAccess && (
            <Button
              onClick={() => navigate("/appointment/create")}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Add New Appointment
            </Button>
          )} */}
        </GridLayoutItem>

        {/* Filter card sits between title and grid */}
        <GridLayoutItem>
          <AppointmentGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const AppointmentGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  // const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  // const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const AppointmentList = useAppSelector(
    (state) => state.appointment.AppointmentList,
  );
  const loading = useAppSelector((state) => state.appointment.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    dispatch(setCurrentPage(1));
  };

  // const MyEditCommandCell = (props: GridCellProps) => {
  //   const handleEdit = (ID: number) => {
  //     navigate("/appointment/edit", { state: { AppointmentID: ID } });
  //   };
  //   const handleOpenDeleteDialog = (ID: number) => {
  //     dispatch(openDialog("deleteAppointment"));
  //     dispatch(setAppointmentID(ID));
  //   };
  //   return (
  //     <td className={`action-td ${props.className}`} style={{ ...props.style }}>
  //       {isEditAccess && (
  //         <IconButton
  //           type="button"
  //           fillMode="flat"
  //           size="small"
  //           title="Edit"
  //           onClick={() => handleEdit(props.dataItem.ID)}
  //           icon={FiEdit}
  //           iconClassName="absolute-position"
  //           iconStyle={{ fontSize: "20px" }}
  //         />
  //       )}
  //       {isDeleteAccess && (
  //         <IconButton
  //           type="button"
  //           fillMode="flat"
  //           size="small"
  //           title="Delete"
  //           className="color-red"
  //           onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
  //           icon={MdDelete}
  //           iconClassName="absolute-position"
  //           iconStyle={{ fontSize: "24px" }}
  //         />
  //       )}
  //     </td>
  //   );
  // };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 7 || props.index === 8 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => (
    <td {...props.tdProps} colSpan={1} className={"table-cell"}>
      {props.children}
    </td>
  );

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}

      <AppointmentFilterCard />

      {AppointmentList && AppointmentList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 300px)" }}
          // filterable={true}
          className="responsive-table"
          filter={filter}
          data={AppointmentList}
          onFilterChange={handleFilterChange}
          skip={currentPage * pageLimit}
          take={pageLimit}
          total={AppointmentList[0]?.ItemCount || AppointmentList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, 20, 50],
            pageSizeValue: pageLimit,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="PatientName"
            title="Patient Name"
            width={"200px"}
            cell={(props: any) => (
              <td>
                {/* {isEditAccess ? (
                  <div
                    className="hoverableName"
                    onClick={() =>
                      navigate("/appointment/edit", {
                        state: { AppointmentID: props.dataItem?.ID },
                      })
                    }
                  >
                    {props.dataItem?.PatientName}
                  </div>
                ) : (
                  <div className="normalName">
                    {props.dataItem?.PatientName}
                  </div>
                )} */}
                <div className="normalName">{props.dataItem?.PatientName}</div>
              </td>
            )}
          />
          <Column field="DoctorName" title="Doctor" width={"180px"} />
          <Column field="MobileNo" title="Mobile No" width={"140px"} />
          <Column field="EmailID" title="Email ID" width={"200px"} />
          <Column
            field="Date"
            title="Date"
            width={"130px"}
            filterable={false}
            cells={{
              data: (props: any) => <td>{props.dataItem?.Date}</td>,
            }}
          />
          <Column
            field="AvailableTimeSlot"
            title="Time Slot"
            width={"130px"}
            filterable={false}
          />
          <Column
            field="Fees"
            title="Fees (₹)"
            width={"110px"}
            filterable={false}
          />
          {/* {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              filterable={false}
              locked={true}
            />
          )} */}
        </KendoGrid>
      ) : (
        !loading && <AlertBox />
      )}
    </>
  );
};

// ── Delete Dialog ─────────────────────────────────────────────────────────────
const DeleteAppointmentDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const AppointmentID = useAppSelector(
    (state) => state.appointment.AppointmentID,
  );
  const loading = useAppSelector((state) => state.appointment.loading);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const FilterAppointment = useAppSelector(
    (state) => state.appointment.FilterAppointment,
  );

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      await dispatch(deleteAppointment(ID));
      dispatch(getAllAppointments({ ...FilterAppointment }));
      dispatch(closeDialog());
      dispatch(clearAppointmentID());
    }
  };

  return (
    <>
      {dialogName === "deleteAppointment" && (
        <AppDialog>
          <>
            <Typography.h5>Delete Appointment</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this appointment?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{ display: "flex", justifyContent: "end", gap: "10px" }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(AppointmentID)}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Appointment;
